import React, { Key, useEffect, useState } from "react";
import {
    useGetUserInfoQuery,
    useSetUserLanguageMutation,
} from "../../../app/rootReducer";
import { LanguageInterface } from "../../interfaces/shared/LanguageInterface";
import { CountryInterface } from "../../interfaces/shared/CountryInterface";
import { useStringTranslation } from "../../hooks/useStringTranslation";

interface LanguagePickerProps {
    active: boolean;
    setActive: React.Dispatch<React.SetStateAction<boolean>>;
    setCountryActive: React.Dispatch<React.SetStateAction<boolean>>;
}

const openLanguagePicker = (
    active: boolean,
    setActive: React.Dispatch<React.SetStateAction<boolean>>,
    setCountryActive: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
    setActive(!active);
    setCountryActive(false);
};

const setLanguage = (
    language: LanguageInterface,
    country: CountryInterface,
    setUserLanguageTrigger: ReturnType<typeof useSetUserLanguageMutation>[0],
    setActiveLanguageState: React.Dispatch<
        React.SetStateAction<LanguageInterface | null>
    >,
): void => {
    setActiveLanguageState(language);
    setUserLanguageTrigger({
        languageId: language.id,
        countryId: country.id,
    });
};

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
    active,
    setActive,
    setCountryActive,
}) => {
    const { t } = useStringTranslation();
    const [setUserLanguageTrigger] = useSetUserLanguageMutation();

    const { data } = useGetUserInfoQuery();
    const { languages, activeLanguage, activeCountry } = data || {
        languages: [],
        activeLanguage: null,
        activeCountry: null,
    };

    const [activeLanguageState, setActiveLanguageState] =
        useState<LanguageInterface | null>(activeLanguage);

    useEffect(() => {
        setActiveLanguageState(activeLanguage);
    }, [activeLanguage]);

    if (
        !languages ||
        languages.length <= 1 ||
        !activeLanguageState ||
        !activeCountry
    ) {
        return null;
    }

    return (
        <div className="c-language-picker__wrapper">
            <div className="c-language-picker__label">
                <span>{t("language")}:</span>
            </div>
            <div className={"c-language-picker " + (active ? "is-active" : "")}>
                <button
                    className="c-language-picker__btn"
                    onClick={() =>
                        openLanguagePicker(active, setActive, setCountryActive)
                    }
                >
                    <div>
                        <span>{activeLanguageState.name}</span>
                    </div>
                    <img
                        className="img-arrow"
                        src="/images/arrow-down.svg"
                        alt=""
                    />
                </button>
                <div className="c-language-picker__container">
                    <ul>
                        {languages
                            .filter(
                                (language: LanguageInterface) =>
                                    language.id !== activeLanguageState.id,
                            )
                            .map(
                                (
                                    language: LanguageInterface,
                                    index: Key | null | undefined,
                                ) => (
                                    <li
                                        key={index}
                                        onClick={() => {
                                            setActive(!active);
                                            setLanguage(
                                                language,
                                                activeCountry,
                                                setUserLanguageTrigger,
                                                setActiveLanguageState,
                                            );
                                        }}
                                    >
                                        <span>{language.name}</span>
                                    </li>
                                ),
                            )}
                    </ul>
                </div>
            </div>
        </div>
    );
};
